/**
 * PromptTuningCard — surfaces the weekly AI Prompt Tuner's suggestions
 * (from B10). Employers/admins see clusters of AI-feedback complaints with
 * an LLM-drafted refinement per cluster, and can Accept (mark implemented)
 * or Dismiss individual suggestions.
 *
 * Mounts on the Skill Center page under the header. Silently no-ops when
 * no pending suggestions exist so the panel doesn't add noise.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Check, X, ChevronDown, ChevronUp, RotateCcw } from "lucide-react";

const FEATURE_LABEL = {
    red_flag: "Red-flag prompt",
    ai_summary: "AI summary prompt",
    skill_match: "Skill-match prompt",
    urgency_task: "Urgency-task prompt",
    suggestion: "Suggestion prompt",
    unknown: "AI output",
};

export default function PromptTuningCard({ isAdmin = false }) {
    const [items, setItems] = useState(null);
    const [expanded, setExpanded] = useState(true);
    const [running, setRunning] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/prompt-tuning-suggestions", { params: { status: "pending", limit: 20 } });
            setItems(data?.items || []);
        } catch { setItems([]); }
    };
    useEffect(() => { load(); }, []);

    const patch = async (sid, status) => {
        try {
            await api.patch(`/prompt-tuning-suggestions/${sid}`, { status });
            setItems((cur) => (cur || []).filter((i) => i.id !== sid));
            toast.success(status === "accepted" ? "Marked as implemented" : "Dismissed");
        } catch { toast.error("Couldn't update suggestion"); }
    };

    const runNow = async () => {
        setRunning(true);
        try {
            const { data } = await api.post("/prompt-tuning-suggestions/run", null, { params: { force: true } });
            toast.success(`Scanned ${data?.stats?.companies_scanned || 0} companies, ${data?.stats?.clusters_inserted || 0} clusters`);
            await load();
        } catch { toast.error("Run failed"); }
        finally { setRunning(false); }
    };

    if (items === null) return null; // initial load
    if (items.length === 0 && !isAdmin) return null;

    return (
        <section className="border border-border bg-white" data-testid="prompt-tuning-card">
            <header className="border-b border-border px-4 py-3 flex items-center gap-3 bg-secondary/40">
                <Sparkles className="w-4 h-4 text-ikb shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ikb">Weekly · from AI feedback</div>
                    <h3 className="font-display font-bold text-sm uppercase tracking-wide truncate">AI Prompt Improvements</h3>
                </div>
                <span className="text-[10px] font-mono bg-ikb text-white px-1.5 py-0.5 rounded" data-testid="prompt-tuning-count">
                    {items.length}
                </span>
                {isAdmin && (
                    <button
                        type="button"
                        onClick={runNow}
                        disabled={running}
                        className="h-7 px-2 border border-border text-[10px] font-mono uppercase tracking-widest hover:border-ikb hover:text-ikb disabled:opacity-50"
                        data-testid="prompt-tuning-run-now"
                        title="Force a fresh clustering pass across all companies"
                    >
                        <RotateCcw className={`w-3 h-3 inline mr-1 ${running ? "animate-spin" : ""}`} />
                        {running ? "Running…" : "Run now"}
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    className="h-7 w-7 grid place-items-center border border-border hover:border-ikb"
                    data-testid="prompt-tuning-toggle"
                    aria-label={expanded ? "Collapse" : "Expand"}
                >
                    {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
            </header>
            {expanded && (
                <>
                    {items.length === 0 ? (
                        <div className="p-6 text-center text-sm text-muted-foreground">
                            No pending suggestions. The tuner runs nightly against last 7 days of 👎 events.
                        </div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {items.map((it) => (
                                <SuggestionRow key={it.id} it={it} onAccept={() => patch(it.id, "accepted")} onDismiss={() => patch(it.id, "dismissed")} />
                            ))}
                        </ul>
                    )}
                </>
            )}
        </section>
    );
}

function SuggestionRow({ it, onAccept, onDismiss }) {
    const [showReasons, setShowReasons] = useState(false);
    const label = FEATURE_LABEL[it.feature] || FEATURE_LABEL.unknown;
    return (
        <li className="p-4 space-y-2" data-testid={`prompt-tuning-row-${it.id}`}>
            <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[10px] font-mono uppercase tracking-widest bg-ikb/10 text-ikb border border-ikb/40 px-1.5 py-0.5">
                    {label}
                </span>
                <span className="text-[10px] font-mono text-muted-foreground">{it.count} complaints · week of {it.week_of}</span>
            </div>
            {it.drafted_suggestion ? (
                <p className="text-sm leading-relaxed" data-testid={`prompt-tuning-draft-${it.id}`}>
                    {it.drafted_suggestion}
                </p>
            ) : (
                <p className="text-sm text-muted-foreground italic">
                    (LLM draft unavailable — read the recruiter complaints below and refine manually.)
                </p>
            )}
            <button
                type="button"
                onClick={() => setShowReasons((s) => !s)}
                className="text-[11px] font-mono text-ikb hover:underline"
                data-testid={`prompt-tuning-reasons-toggle-${it.id}`}
            >
                {showReasons ? "Hide" : "Show"} {it.sample_reasons?.length || 0} recruiter complaints
            </button>
            {showReasons && (
                <ul className="mt-1 space-y-0.5 border-l-2 border-border pl-3">
                    {(it.sample_reasons || []).map((r, i) => (
                        <li key={i} className="text-[12px] text-foreground/80">· {r}</li>
                    ))}
                </ul>
            )}
            <div className="flex items-center gap-2 pt-1">
                <button
                    type="button"
                    onClick={onAccept}
                    className="h-7 px-3 border border-emerald-500 text-emerald-800 hover:bg-emerald-50 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
                    data-testid={`prompt-tuning-accept-${it.id}`}
                >
                    <Check className="w-3 h-3" /> Mark implemented
                </button>
                <button
                    type="button"
                    onClick={onDismiss}
                    className="h-7 px-3 border border-border hover:border-rose-500 hover:text-rose-700 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
                    data-testid={`prompt-tuning-dismiss-${it.id}`}
                >
                    <X className="w-3 h-3" /> Dismiss
                </button>
            </div>
        </li>
    );
}
